import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { amount, scaleOf } from '../lib/format';
import { useSession } from '../lib/session';
import '../styles/composer.css';
import '../styles/documents.css';

interface RouteCfg { type: string; eyebrow: string; title: string; simple?: boolean; dated?: boolean }

const ROUTE_CFG: Record<string, RouteCfg> = {
  '/ventas/cotizaciones/nueva': { type: 'quote', eyebrow: 'Ventas', title: 'New quotation', dated: true },
  '/ventas/contratos/nuevo': { type: 'contract', eyebrow: 'Ventas', title: 'Nuevo contrato', dated: true },
  '/ventas/facturas/nueva': { type: 'invoice', eyebrow: 'Ventas', title: 'Nueva factura', dated: true },
  '/compras/gastos/nuevo': { type: 'expense', eyebrow: 'Compras', title: 'Nuevo gasto', simple: true },
  '/finanzas/pagos/nuevo': { type: 'payment', eyebrow: 'Finanzas', title: 'Registrar cobro', simple: true },
  '/operaciones/loi/nueva': { type: 'loi', eyebrow: 'Operaciones', title: 'New letter of intent' },
};

interface TaxRate { id: string; name: string; ratePct: number; kind: string; isDefault: boolean }
interface Cp { id: string; name: string }

interface Line {
  description: string;
  quantity: number;
  unitPrice: string; // en unidades mayores, tal como se teclea
  discountPct: number;
  taxPct: number;
  taxKind: string;
}

const emptyLine = (): Line => ({ description: '', quantity: 1, unitPrice: '', discountPct: 0, taxPct: 0, taxKind: 'standard' });

export function DocumentComposer() {
  const loc = useLocation();
  const nav = useNavigate();
  const { company } = useSession();
  const cfg = ROUTE_CFG[loc.pathname];

  const [counterparties, setCounterparties] = useState<Cp[]>([]);
  const [taxes, setTaxes] = useState<TaxRate[]>([]);
  const [cpId, setCpId] = useState('');
  const [title, setTitle] = useState('');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState('');
  const [lines, setLines] = useState<Line[]>([emptyLine()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const cur = company?.currency ?? 'AED';
  const scale = scaleOf(cur);

  useEffect(() => {
    if (!company) return;
    api.get<Cp[]>('/counterparties').then(setCounterparties).catch(() => void 0);
    api.get<TaxRate[]>('/tax-rates').then((t) => {
      setTaxes(t);
      const def = t.find((x) => x.isDefault);
      if (def) setLines([{ ...emptyLine(), taxPct: def.ratePct, taxKind: def.kind }]);
    }).catch(() => void 0);
  }, [company]);

  const toMinor = (major: string): number => Math.round((Number(major) || 0) * 10 ** scale);

  const totals = useMemo(() => {
    let subtotal = 0, discount = 0, tax = 0;
    for (const l of lines) {
      const base = l.quantity * toMinor(l.unitPrice);
      const d = Math.round((base * l.discountPct) / 100);
      const taxable = base - d;
      const t = Math.round((taxable * l.taxPct) / 100);
      subtotal += base; discount += d; tax += t;
    }
    return { subtotal, discount, tax, total: subtotal - discount + tax };
  }, [lines, scale]);

  if (!cfg) {
    return <main className="main"><div className="card"><div className="empty">Tipo de documento no reconocido.</div></div></main>;
  }

  const setLine = (i: number, patch: Partial<Line>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const onTax = (i: number, taxId: string) => {
    if (taxId === '') return setLine(i, { taxPct: 0, taxKind: 'out_of_scope' });
    const t = taxes.find((x) => x.id === taxId);
    if (t) setLine(i, { taxPct: t.ratePct, taxKind: t.kind });
  };

  const submit = async () => {
    const valid = lines.filter((l) => l.description.trim().length > 0);
    if (valid.length === 0) { setErr('Add at least one line with an item.'); return; }
    setBusy(true);
    setErr(null);
    try {
      const created = await api.post<{ id: string }>('/documents', {
        type: cfg.type,
        counterpartyId: cpId || undefined,
        title: title.trim() || undefined,
        currency: cur,
        issueDate,
        dueDate: cfg.dated && dueDate ? dueDate : undefined,
        lines: valid.map((l) => ({
          description: l.description.trim(),
          quantity: l.quantity,
          unitPriceMinor: toMinor(l.unitPrice),
          discountPct: l.discountPct,
          taxPct: l.taxPct,
          taxKind: l.taxKind,
        })),
      });
      nav(`/documentos/${created.id}`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No se pudo crear el documento.');
    } finally {
      setBusy(false);
    }
  };

  const currentTaxId = (l: Line): string => {
    if (l.taxKind === 'out_of_scope' || l.taxPct === 0) return '';
    return taxes.find((t) => t.ratePct === l.taxPct && t.kind === l.taxKind)?.id ?? '';
  };

  return (
    <main className="main">
      <button className="doc-back" onClick={() => nav(-1)}>
        <Icon name="arrow-right" size={15} style={{ transform: 'rotate(180deg)' }} /> Volver
      </button>

      <div className="page-head">
        <div className="page-head__title">
          <span className="eyebrow">{cfg.eyebrow}</span>
          <h1 className="h1">{cfg.title}</h1>
        </div>
      </div>

      <div className="composer-grid">
        <div className="col gap-20">
          <div className="card">
            <div className="card__head"><span className="card__title">Datos</span></div>
            <div className="card__body">
              <div className="row gap-12 wrap">
                <div className="field grow" style={{ minWidth: 220, marginBottom: 0 }}>
                  <label>{cfg.type === 'expense' || cfg.type === 'payment' ? 'Proveedor / contraparte' : 'Cliente / contraparte'}</label>
                  <select className="select" value={cpId} onChange={(e) => setCpId(e.target.value)}>
                    <option value="">— Sin especificar —</option>
                    {counterparties.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="field" style={{ width: 170, marginBottom: 0 }}>
                  <label>Fecha</label>
                  <input className="input" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
                </div>
                {cfg.dated && (
                  <div className="field" style={{ width: 170, marginBottom: 0 }}>
                    <label>Vencimiento</label>
                    <input className="input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                  </div>
                )}
              </div>
              <div className="field" style={{ marginTop: 14, marginBottom: 0 }}>
                <label>Item / title</label>
                <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short document description" />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card__head"><span className="card__title">Lines</span></div>
            <div className="card__body">
              <div className="line-editor__head">
                <span>Item</span>
                <span className="r">Cant.</span>
                <span className="r">Precio</span>
                <span className="r">Dto %</span>
                <span>Impuesto</span>
                <span />
              </div>
              {lines.map((l, i) => (
                <div className="line-editor__row" key={i}>
                  <input className="line-input" value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="Description…" />
                  <input className="line-input r" type="number" value={l.quantity} onChange={(e) => setLine(i, { quantity: Number(e.target.value) || 0 })} />
                  <input className="line-input r" type="number" value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: e.target.value })} placeholder="0" />
                  <input className="line-input r" type="number" value={l.discountPct} onChange={(e) => setLine(i, { discountPct: Number(e.target.value) || 0 })} />
                  <select className="line-input line-input--sel" value={currentTaxId(l)} onChange={(e) => onTax(i, e.target.value)}>
                    <option value="">Sin impuesto</option>
                    {taxes.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.ratePct}%)</option>)}
                  </select>
                  <button className="line-del" onClick={() => setLines((prev) => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev)} title="Remove line">
                    <Icon name="x" size={15} />
                  </button>
                </div>
              ))}
              <button className="add-line" onClick={() => setLines((prev) => [...prev, emptyLine()])}>
                <Icon name="plus" size={15} /> Add line
              </button>
            </div>
          </div>
        </div>

        {/* Resumen */}
        <div className="composer-side">
          <div className="card">
            <div className="card__head"><span className="card__title">Resumen</span></div>
            <div className="card__body">
              <div className="totals" style={{ padding: 0 }}>
                <div className="totals__row"><span className="muted">Subtotal</span><span className="num">{amount(totals.subtotal, cur)}</span></div>
                {totals.discount > 0 && <div className="totals__row"><span className="muted">Descuento</span><span className="num">−{amount(totals.discount, cur)}</span></div>}
                <div className="totals__row"><span className="muted">Impuestos</span><span className="num">{amount(totals.tax, cur)}</span></div>
                <div className="totals__row totals__grand"><span>Total</span><span className="num">{amount(totals.total, cur)} {cur}</span></div>
              </div>

              {err && <div className="act-error" style={{ marginTop: 14 }}><Icon name="alert" size={16} />{err}</div>}

              <button className="btn btn--primary" style={{ width: '100%', marginTop: 16 }} onClick={submit} disabled={busy}>
                {busy ? 'Guardando…' : 'Crear borrador'}
              </button>
              <p className="faint" style={{ fontSize: 12, marginTop: 10, textAlign: 'center' }}>
                It is created as a draft. You can review and issue it later.
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
